import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { CHART_COLORS } from '../../../consts.js';

const MARGIN = { top: 24, right: 24, bottom: 48, left: 72 };
const BAR_FILL = 0.72;
const BAR_COLORS = CHART_COLORS;

function scaleX(units) {
  if (units === 'Mt') return 1e9;
  if (units === 'kt') return 1e6;
  if (units === 't') return 1e3;
  return 1;
}

function autoUnits(maxKg) {
  if (maxKg >= 1e9) return 'Mt';
  if (maxKg >= 1e6) return 'kt';
  if (maxKg >= 1e3) return 't';
  return 'kg';
}

function niceTicks(min, max, count = 5) {
  const range = max - min || 1;
  const rawStep = range / (count - 1);
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const nicedStep = Math.ceil(rawStep / magnitude) * magnitude;
  const start = Math.floor(min / nicedStep) * nicedStep;
  const ticks = [];
  for (let i = 0; ticks.length < count + 2; i++) {
    const t = start + i * nicedStep;
    ticks.push(t);
    if (t > max + nicedStep) break;
  }
  return ticks;
}

function formatNumber(n) {
  if (Math.abs(n) >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (Math.abs(n) >= 10) return n.toFixed(1);
  return n.toFixed(2);
}

function truncate(str, max) {
  return str.length > max ? str.slice(0, max - 1) + '…' : str;
}

function formatCost(n) {
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}K`;
  return `${sign}$${formatNumber(abs)}`;
}

const Tooltip = ({ bar, x, y, xUnits, yAxisLabel, visible }) => {
  if (!visible || !bar) return null;
  const co2 = formatNumber(bar.annualCo2Avoided / scaleX(xUnits));
  return (
    <div
      className="absolute z-10 pointer-events-none bg-base-100 border border-base-300 rounded shadow-md text-xs p-2 min-w-44"
      style={{ left: x + 10, top: y - 8 }}
    >
      <p className="font-semibold mb-1">{bar.name}</p>
      <p>{formatNumber(bar.coac)} {yAxisLabel}</p>
      <p>{co2} {xUnits} CO₂e/yr avoided</p>
      <p className="mt-1 text-base-content/60">{formatCost(bar.annualCostPremium)}/yr cost premium</p>
    </div>
  );
};

Tooltip.propTypes = {
  bar: PropTypes.object,
  x: PropTypes.number,
  y: PropTypes.number,
  xUnits: PropTypes.string,
  yAxisLabel: PropTypes.string,
  visible: PropTypes.bool,
};



const AbatementPotentialPlot = ({ maccBars, yAxisLabel }) => {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(600);
  const [tooltip, setTooltip] = useState({ bar: null, x: 0, y: 0, visible: false });

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(entries => {
      setWidth(entries[0].contentRect.width);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  if (!maccBars || maccBars.length === 0) {
    return (
      <p className="text-sm text-base-content/50 italic">
        No abatement data available.
      </p>
    );
  }

  const maxCo2Kg = Math.max(...maccBars.map(b => b.annualCo2Avoided));
  const xUnits = autoUnits(maxCo2Kg);
  const divisor = scaleX(xUnits);

  const coacValues = maccBars.map(b => b.coac);
  const yMin = Math.min(0, ...coacValues);
  const yMax = Math.max(0, ...coacValues);

  const plotW = width - MARGIN.left - MARGIN.right;
  const plotH = 280;
  const svgH = plotH + MARGIN.top + MARGIN.bottom;

  const slotW = plotW / maccBars.length;

  const toSvgY = dataY => {
    const yRange = yMax - yMin || 1;
    return plotH - ((dataY - yMin) / yRange) * plotH;
  };

  const zeroY = toSvgY(0);

  const rects = maccBars.map((bar, i) => {
    const slotCenterX = (i + 0.5) * slotW;
    const barW = Math.max((bar.annualCo2Avoided / maxCo2Kg) * slotW * BAR_FILL, 2);
    const barX = slotCenterX - barW / 2;
    const svgY = toSvgY(bar.coac);
    const rectY = bar.coac >= 0 ? svgY : zeroY;
    const rectH = Math.max(Math.abs(svgY - zeroY), 1);
    const co2Label = `${formatNumber(bar.annualCo2Avoided / divisor)} ${xUnits}`;
    return {
      bar, barX, barW, rectY, rectH, slotCenterX,
      color: BAR_COLORS[i % BAR_COLORS.length],
      co2Label,
    };
  });

  const yTicks = niceTicks(yMin, yMax, 5).filter(t => t >= yMin && t <= yMax + (yMax - yMin) * 0.05);

  const handleMouseEnter = (e, bar) => {
    const rect = containerRef.current.getBoundingClientRect();
    setTooltip({ bar, x: e.clientX - rect.left, y: e.clientY - rect.top, visible: true });
  };

  const handleMouseMove = e => {
    if (!tooltip.visible) return;
    const rect = containerRef.current.getBoundingClientRect();
    setTooltip(prev => ({ ...prev, x: e.clientX - rect.left, y: e.clientY - rect.top }));
  };

  const handleMouseLeave = () => setTooltip(prev => ({ ...prev, visible: false }));

  return (
    <div ref={containerRef} className="relative w-full" onMouseMove={handleMouseMove}>
      <svg width={width} height={svgH} className="overflow-visible">
        <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
          {/* y grid lines */}
          {yTicks.map(t => (
            <line key={t} x1={0} x2={plotW} y1={toSvgY(t)} y2={toSvgY(t)} stroke="#e5e7eb" strokeWidth={1} />
          ))}

          {/* zero line */}
          <line x1={0} x2={plotW} y1={zeroY} y2={zeroY} stroke="#9ca3af" strokeWidth={1.5} />

          {/* slot dividers */}
          {maccBars.map((_, i) => i > 0 && (
            <line key={i} x1={i * slotW} x2={i * slotW} y1={0} y2={plotH} stroke="#f3f4f6" strokeWidth={1} strokeDasharray="4 3" />
          ))}

          {/* bars + annotations */}
          {rects.map(({ bar, barX, barW, rectY, rectH, slotCenterX, color, co2Label }) => {
            const barCenterY = rectY + rectH / 2;
            const showInside = rectH > 22 && barW > 30;
            return (
              <g key={bar.id}>
                <rect
                  x={barX} y={rectY}
                  width={barW} height={rectH}
                  fill={color}
                  opacity={tooltip.bar?.id === bar.id ? 1 : 0.82}
                  className="cursor-pointer transition-opacity"
                  onMouseEnter={e => handleMouseEnter(e, bar)}
                  onMouseLeave={handleMouseLeave}
                />
                {showInside && (
                  <text
                    x={slotCenterX} y={barCenterY}
                    textAnchor="middle" dominantBaseline="middle"
                    fontSize={11} fill="white" style={{ pointerEvents: 'none' }}
                  >
                    {co2Label}
                  </text>
                )}
                <text x={slotCenterX} y={plotH + 30} textAnchor="middle" fontSize={11} fill="#6b7280">
                  {truncate(bar.name, Math.max(6, Math.floor(slotW / 7)))}
                </text>
              </g>
            );
          })}

          {/* y axis */}
          <line x1={0} x2={0} y1={0} y2={plotH} stroke="#6b7280" strokeWidth={1} />
          {yTicks.map(t => (
            <g key={t} transform={`translate(0,${toSvgY(t)})`}>
              <line x1={-4} x2={0} stroke="#6b7280" strokeWidth={1} />
              <text x={-8} textAnchor="end" dominantBaseline="middle" fontSize={11} fill="#6b7280">
                {formatNumber(t)}
              </text>
            </g>
          ))}
          <text
            transform={`translate(${-52},${plotH / 2}) rotate(-90)`}
            textAnchor="middle" fontSize={11} fill="#6b7280"
          >
            {yAxisLabel}
          </text>

          {/* x axis */}
          <line x1={0} x2={plotW} y1={plotH} y2={plotH} stroke="#6b7280" strokeWidth={1} />
        </g>
      </svg>

      <Tooltip
        bar={tooltip.bar}
        x={tooltip.x}
        y={tooltip.y}
        xUnits={xUnits}
        yAxisLabel={yAxisLabel}
        visible={tooltip.visible}
      />

    </div>
  );
};

AbatementPotentialPlot.propTypes = {
  maccBars: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      coac: PropTypes.number.isRequired,
      annualCo2Avoided: PropTypes.number.isRequired,
      annualCostPremium: PropTypes.number.isRequired,
    })
  ),
  yAxisLabel: PropTypes.string,
};

export default AbatementPotentialPlot;
